import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, theme } from 'antd';
import {
  notificationDeepLink,
  useMarkNotificationReadMutation,
  type NotificationView,
} from '@entities/notification';
import {
  formatNotificationTime,
  notificationLabelKey,
  notificationVisual,
} from '../lib/format';

const { Text } = Typography;

interface NotificationItemProps {
  notification: NotificationView;
  onAfterAction?: () => void;
  compact?: boolean;
}

export function NotificationItem({ notification, onAfterAction, compact }: NotificationItemProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const markRead = useMarkNotificationReadMutation();
  const [hovered, setHovered] = useState(false);

  const link = notificationDeepLink(notification);
  const { icon, accent, rgb } = notificationVisual(notification.type);
  const unread = !notification.read;

  const handleClick = () => {
    if (unread) markRead.mutate(notification.id);
    if (link) navigate(link);
    onAfterAction?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        textAlign: 'left',
        cursor: 'pointer',
        padding: compact ? '10px 12px 10px 15px' : '14px 16px 14px 19px',
        borderRadius: 14,
        // Unread carries the type's hue; read settles into the plain container
        // colour so the eye lands on what still needs attention.
        background: unread
          ? `linear-gradient(135deg, rgba(${rgb},0.14), rgba(${rgb},0.02) 70%), ${token.colorBgContainer}`
          : token.colorBgContainer,
        border: `1px solid ${hovered ? accent : token.colorBorderSecondary}`,
        boxShadow: hovered ? token.boxShadowSecondary : token.boxShadowTertiary,
        transform: hovered ? 'translateY(-1px)' : 'none',
        transition:
          'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease, background 180ms ease',
      }}
    >
      {/* Accent rail — the only marker that survives at a glance while scanning. */}
      {unread ? (
        <span
          aria-hidden
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }}
        />
      ) : null}

      <span
        aria-hidden
        style={{
          width: compact ? 32 : 38,
          height: compact ? 32 : 38,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 11,
          background: unread ? token.colorBgContainer : token.colorFillQuaternary,
          color: unread ? accent : token.colorTextTertiary,
          fontSize: compact ? 15 : 17,
          boxShadow: unread ? token.boxShadowTertiary : 'none',
          transition: 'color 180ms ease, background 180ms ease',
        }}
      >
        {icon}
      </span>

      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: unread ? accent : token.colorTextTertiary,
            }}
          >
            {t(notificationLabelKey(notification.type))}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Text type="secondary" style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
              {formatNotificationTime(notification.createdAt, i18n.language || 'en')}
            </Text>
            {unread ? (
              <span
                aria-hidden
                style={{ width: 7, height: 7, borderRadius: '50%', background: accent }}
              />
            ) : null}
          </span>
        </span>

        <span
          style={{
            fontSize: 13.5,
            fontWeight: unread ? 600 : 500,
            lineHeight: 1.35,
            color: token.colorText,
          }}
        >
          {notification.title}
        </span>

        {notification.message ? (
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
            {notification.message}
          </Text>
        ) : null}
      </span>
    </button>
  );
}
